import { useQuery } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export interface SearchResult {
  type: "customer" | "quote";
  contact_id: number;
  quote_id?: string;
  quote_number?: string;
  amount?: number;
  customer_name: string;
  ghosted?: boolean;
  email: string;
  phone?: string;
  status?: string;
  match_field: string;
  rank: number;
}

type RawSearchResult = {
  type: "customer" | "quote";
  rank: number;
  contact: {
    id: number;
    full_name: string;
    email: string;
    phone?: string;
  };
  ghosted?: boolean;
  quote: null | {
    quote_id: string;
    object_number?: string;
    total_usd?: number;
    status?: string;
  };
};

function matchFieldFromRank(rank: number) {
  if (rank >= 100) return "Quote ID";
  if (rank >= 95) return "Email";
  if (rank >= 90) return "Phone";
  if (rank >= 85) return "Name";
  return "Contains";
}

function mapResult(row: RawSearchResult): SearchResult {
  return {
    type: row.quote ? "quote" : "customer",
    contact_id: row.contact.id,
    quote_id: row.quote?.quote_id,
    quote_number: row.quote?.object_number || row.quote?.quote_id,
    amount: row.quote?.total_usd,
    customer_name: row.contact.full_name,
    ghosted: row.ghosted,
    email: row.contact.email,
    phone: row.contact.phone,
    status: row.quote?.status,
    match_field: matchFieldFromRank(row.rank),
    rank: row.rank,
  };
}

export function useSearchQuotes(query: string, enabled = true) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["search", query],
    queryFn: async () => {
      const res = await fetch(`/api/cockpit/search?q=${encodeURIComponent(query)}&limit=20`, {
        headers,
      });
      if (!res.ok) throw new Error("Search failed");
      const payload = (await res.json()) as { results: RawSearchResult[]; total_matches: number };
      return {
        results: payload.results.map(mapResult),
        total_matches: payload.total_matches,
      };
    },
    enabled: enabled && query.length >= 2,
    staleTime: 30_000,
  });
}
